/* ════════════════════════════════════════════════════════════════
   LFJ — MENU-ONLY AMBIENT MIX PLAYER  (B16, revised Checkpoint 22)
   "I want this to be able to play a pokemon black and white or
   pokemon pearl and diamond music on the website naturally... the
   same mix is the first mix on the FM page too."

   Checkpoint 22 correction: this was running site-wide (every room
   except FM), at a volume (0.55) loud enough to fight with whatever
   a visitor is actually trying to focus on inside a room. Jaydo:
   "change it so the pokemon music only plays quietly in the main
   menu. do not let it overpower the website." Scope narrowed to
   data-room="menu" only, volume dropped to a real background level,
   and a soft procedural room-tone layer added underneath (see
   buildAmbientBed() below) so the mix doesn't sit in dead silence —
   it reads as a console humming/breathing, not just an mp3 on a
   page. No new asset upload needed for that layer; it's synthesized
   with Web Audio so it always ships even before Jaydo uploads more
   source files.

   Loads assets/mixes.json (same file FM's Mixes tab reads — one
   real source of truth, not two copies of the same data):

   - Menu room ONLY (data-room="menu") — everywhere else, including
     FM (which owns its own hardened player for this exact catalog
     via lf-playlist.js / lf-fm-player.js), stays silent from this
     script. One audio engine per catalog, one room for ambient.
   - One shared queue across BOTH mixes (Black & White + Diamond &
     Pearl) — "let the music play in between both mixes... the
     songs are shuffled on random."
   - Shuffled fresh every new site VISIT (sessionStorage — cleared
     when the tab/browser closes); position/order persists across
     repeat visits to the menu within that same browser session.
   - Autoplay-safe: browsers block unmuted autoplay before a real
     user gesture. Starts muted (always allowed), unmutes on the
     first real click/key/touch anywhere on the page — same
     "gesture unlocks audio" pattern already used for the FM page.
═══════════════════════════════════════════════════════════════ */

(function () {
  var ROOM = document.body ? document.body.getAttribute('data-room') : null;
  if (ROOM !== 'menu') return; // Checkpoint 22 — menu-only, quiet, non-overpowering

  var MIXES_URL = window.LF_MIXES_URL;
  if (!MIXES_URL) return; // not wired on this page/layout — nothing to do

  var SS_KEY = 'lf_ambient_queue_v1';      // per-visit shuffle order (sessionStorage)
  var LS_KEY = 'lf_ambient_position_v1';   // cross-page resume position (localStorage)
  var MUTE_KEY = 'lf_ambient_muted_v1';

  var state = {
    tracks: [],       // raw entries from mixes.json, in file order
    order: [],         // shuffled index order for this visit
    pos: 0,             // position within `order`
    audio: null,
    unlocked: false,
    // REVISEMENT — was 0.55 (site-wide, loud), then 0.22 (Checkpoint
    // 22, "quiet nostalgic bed"). Still too present per direct
    // feedback ("make the main menu music even more quiet with
    // ambient sounds"). 0.10 reads as genuinely background — audible
    // as texture, not as "a song is playing."
    savedVolume: 0.10
  };

  function shuffledIndices(n) {
    var arr = [];
    for (var i = 0; i < n; i++) arr.push(i);
    for (var i = arr.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var tmp = arr[i]; arr[i] = arr[j]; arr[j] = tmp;
    }
    return arr;
  }

  function loadVisitOrder(total) {
    try {
      var raw = sessionStorage.getItem(SS_KEY);
      if (raw) {
        var parsed = JSON.parse(raw);
        if (parsed && Array.isArray(parsed.order) && parsed.order.length === total) {
          return parsed.order;
        }
      }
    } catch (e) { /* fall through to a fresh shuffle */ }
    var fresh = shuffledIndices(total);
    try { sessionStorage.setItem(SS_KEY, JSON.stringify({ order: fresh })); } catch (e) {}
    return fresh;
  }

  function saveResumePoint(track, currentTime) {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify({
        url: track.url,
        pos: state.pos,
        time: currentTime || 0,
        savedAt: Date.now()
      }));
    } catch (e) {}
  }

  function readResumePoint() {
    try {
      var raw = localStorage.getItem(LS_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      // Only trust a resume point if it's recent (same browsing
      // session in practice) — an hour+ old means "not really a
      // continuation anymore," just start fresh from the shuffle.
      if (!parsed || (Date.now() - parsed.savedAt) > 60 * 60 * 1000) return null;
      return parsed;
    } catch (e) { return null; }
  }

  // REVISEMENT — was `=== '1'` with no stored value defaulting to
  // *unmuted* (autoplay-on, just quiet). Direct ask: "the pokemon
  // music on the menu enter is to now be silenced unless toggled
  // on." Default now flips the other way: nothing stored yet =
  // muted. Only an explicit "0" (the visitor actively turned it on
  // at some point) plays it.
  function isMuted() {
    try {
      var v = localStorage.getItem(MUTE_KEY);
      return v === null ? true : v === '1';
    } catch (e) { return true; }
  }
  function setMuted(v) {
    try { localStorage.setItem(MUTE_KEY, v ? '1' : '0'); } catch (e) {}
  }

  function currentTrack() {
    var idx = state.order[state.pos];
    return state.tracks[idx];
  }

  function playCurrent(resumeAt) {
    var track = currentTrack();
    if (!track || !state.audio) return;
    if (state.audio.src !== track.url) state.audio.src = track.url;
    if (resumeAt && resumeAt > 0) {
      try { state.audio.currentTime = resumeAt; } catch (e) {}
    }
    var p = state.audio.play();
    if (p && p.catch) p.catch(function () { /* still locked — waits for gesture */ });
  }

  function advance() {
    state.pos = (state.pos + 1) % state.order.length;
    // Wrapped back to the start of the shuffled queue — reshuffle so
    // the "loop" doesn't play in the exact same order every lap.
    if (state.pos === 0) {
      state.order = shuffledIndices(state.tracks.length);
      try { sessionStorage.setItem(SS_KEY, JSON.stringify({ order: state.order })); } catch (e) {}
    }
    saveResumePoint(currentTrack(), 0);
    playCurrent(0);
  }

  /* Checkpoint 22 — procedural nostalgic room-tone/vinyl-crackle bed,
     mixed just underneath the mix track. Built with Web Audio (noise
     buffer -> lowpass filter -> gentle amplitude flutter -> gain)
     instead of a shipped mp3 — needs no manual upload, and a soft
     filtered-noise bed like this is exactly the "warm tape hiss"
     texture that makes a loop feel like it's coming from an old
     console speaker rather than a clean streamed file. Fails silent
     (try/catch) on any browser without Web Audio support — the mix
     track above still plays fine on its own either way. */
  function buildAmbientBed() {
    try {
      var AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return null;
      var ctx = new AC();

      var bufSize = 2 * ctx.sampleRate;
      var buf = ctx.createBuffer(1, bufSize, ctx.sampleRate);
      var data = buf.getChannelData(0);
      for (var i = 0; i < bufSize; i++) data[i] = (Math.random() * 2 - 1) * 0.5;

      var noise = ctx.createBufferSource();
      noise.buffer = buf;
      noise.loop = true;

      var filter = ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = 1200; // warm, no harsh hiss

      var flutter = ctx.createGain();
      flutter.gain.value = 1;
      var lfo = ctx.createOscillator();
      lfo.frequency.value = 0.13; // slow, barely perceptible breathing
      var lfoGain = ctx.createGain();
      lfoGain.gain.value = 0.35;
      lfo.connect(lfoGain);
      lfoGain.connect(flutter.gain);

      var master = ctx.createGain();
      master.gain.value = 0; // fades in once unlocked

      noise.connect(filter);
      filter.connect(flutter);
      flutter.connect(master);
      master.connect(ctx.destination);

      return { ctx: ctx, noise: noise, lfo: lfo, master: master, started: false };
    } catch (e) { return null; }
  }

  function startAmbientBed(bed) {
    if (!bed || bed.started) return;
    try {
      bed.started = true;
      bed.noise.start();
      bed.lfo.start();
      // Quiet floor — this is texture under the music, not a second
      // song. Ramp in over a couple seconds so it never pops in.
      bed.master.gain.linearRampToValueAtTime(0.07, bed.ctx.currentTime + 2.5); // REVISEMENT: bumped from 0.05 as music volume dropped, so ambient texture stays felt relative to the quieter music, not just quieter overall
    } catch (e) {}
  }

  function setAmbientBedMuted(bed, muted) {
    if (!bed) return;
    try {
      var target = muted ? 0 : 0.07; // REVISEMENT: matches startAmbientBed's new target
      bed.master.gain.linearRampToValueAtTime(target, bed.ctx.currentTime + 0.6);
    } catch (e) {}
  }

  function buildAudioEl() {
    var el = document.createElement('audio');
    el.id = 'lf-ambient-audio';
    el.preload = 'auto';
    el.muted = true; // required for the initial autoplay attempt to be allowed
    el.volume = state.savedVolume;
    el.style.display = 'none';
    el.addEventListener('ended', advance);
    document.body.appendChild(el);
    return el;
  }

  function unlockAudio() {
    if (state.unlocked || !state.audio) return;
    state.unlocked = true;
    if (!isMuted()) state.audio.muted = false;
    var p = state.audio.play();
    if (p && p.catch) p.catch(function () {});
    if (!isMuted()) startAmbientBed(state.bed);
  }

  /* CP72 — nothing audible until the loading screen has fully finished.
     A gesture during loading (Press Start tap, nav click) is remembered, but the unmute
     only happens once lf:menu:ready / lf:page:reveal fires. */
  function loadFinished() {
    return !!window.LFJ_LOAD_DONE || document.body.classList.contains('lfj-content-ready');
  }
  function wireGestureUnlock() {
    var events = ['click', 'keydown', 'touchstart'], gestured = false, done = false;
    function tryUnlock() {
      if (done || !gestured || !loadFinished()) return;
      done = true;
      events.forEach(function (ev) { document.removeEventListener(ev, handler); });
      unlockAudio();
    }
    function handler() { gestured = true; tryUnlock(); }
    events.forEach(function (ev) { document.addEventListener(ev, handler, { passive: true }); });
    window.addEventListener('lf:menu:ready', tryUnlock);
    window.addEventListener('lf:page:reveal', tryUnlock);
  }

  function wirePersistOnLeave() {
    var save = function () {
      var track = currentTrack();
      if (track && state.audio) saveResumePoint(track, state.audio.currentTime);
    };
    window.addEventListener('pagehide', save);
    window.addEventListener('beforeunload', save);
    setInterval(save, 5000); // safety net if a link intercepts navigation before pagehide fires
  }

  /* Small optional control so Jaydo/visitors can mute the ambient
     track without it being the only way to discover it exists —
     reuses the same pixel-button visual language as the color-mode
     toggle (assets/lf-color-mode.js) rather than inventing a new
     control style.
     REVISEMENT — was mounted directly onto .lfj-console-body (which
     put it wherever DOM append order happened to land it, after the
     footer). Now mounts into the shared .lfj-toggle-cluster instead,
     alongside the mode toggle — direct ask: "let all the toggle
     buttons be in one toggle area like a dsi where the start and
     selection is in the middle on the bottom." */
  function mountToggle() {
    var cluster = document.querySelector('.lfj-toggle-cluster')
      || document.querySelector('.lfj-console-body')
      || document.body;
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'lf-btn lf-ambient-toggle';
    btn.setAttribute('aria-label', 'Toggle background music');
    btn.textContent = isMuted() ? '\u266A OFF' : '\u266A ON';
    btn.addEventListener('click', function () {
      var muted = !isMuted();
      setMuted(muted);
      if (state.audio) state.audio.muted = muted;
      setAmbientBedMuted(state.bed, muted);
      btn.textContent = muted ? '\u266A OFF' : '\u266A ON';
    });
    cluster.appendChild(btn);
  }

  function init(data) {
    var tracks = (data && Array.isArray(data.mixes)) ? data.mixes.filter(function (t) {
      return t && t.url;
    }) : [];
    if (!tracks.length) return; // mixes.json not populated yet on this deploy — nothing to play

    state.tracks = tracks;
    state.order = loadVisitOrder(tracks.length);
    state.audio = buildAudioEl();
    state.bed = buildAmbientBed();

    var resume = readResumePoint();
    if (resume) {
      var idx = state.tracks.findIndex(function (t) { return t.url === resume.url; });
      if (idx !== -1) {
        var orderIdx = state.order.indexOf(idx);
        if (orderIdx !== -1) state.pos = orderIdx;
        // Real elapsed time is lost across a page load — resume at
        // the saved position but not exactly to the frame; close
        // enough that it reads as "the same song kept going," not
        // "it started over."
        playCurrent(resume.time || 0);
      } else {
        playCurrent(0);
      }
    } else {
      playCurrent(0);
    }

    state.audio.muted = true; // stays true until a real gesture (see wireGestureUnlock)
    if (isMuted()) state.audio.muted = true; // respect an explicit prior mute even after unlock

    wireGestureUnlock();
    wirePersistOnLeave();
    mountToggle();
  }

  fetch(MIXES_URL)
    .then(function (r) { return r.json(); })
    .then(init)
    .catch(function () { /* fetch failed — silently skip ambient audio, not a page-breaking error */ });
})();
